/**
 * TargetTablePanel — Edit the job's target_table field.
 *
 * Rendered in the "Target" tab of JobEditor. Split out of ColumnMappingGrid
 * (which used to own both target-table selection AND column auto-mapping) so
 * each tab has a single responsibility: this tab only sets/saves
 * target_table; the Column Mapping tab's own "Auto-Map Columns" button
 * re-reads whatever target_table is saved here to (re)generate
 * column_mappings.
 *
 * A Target Schema dropdown (populated from the target connection's real,
 * non-system schemas) lets the user pick which schema the job's table lives
 * in. The table name itself is still free text (any name — existing or
 * brand-new) plus a "Browse Existing Tables" convenience dropdown scoped to
 * the selected schema that fills the input — selecting from the dropdown
 * does NOT auto-save; the user still clicks Save, consistent with every
 * other job-editor panel. If the chosen name doesn't match an existing table
 * in the selected schema, it is auto-created there the first time this job
 * runs (see Column Mapping tab's "Auto-Map Columns").
 */

import { useCallback, useEffect, useState } from "react";
import { Database, Loader2, Save } from "lucide-react";
import type { PEJob } from "../../../features/pipeline-engineer/types";
import {
  updateJob,
  listTargetSchemas,
} from "../../../features/pipeline-engineer/api/pe-client";
import { useIntrospectTask } from "../../../features/pipeline-engineer/useAgenticApi";
import {
  splitSchemaTable,
  joinSchemaTable,
} from "../../../features/pipeline-engineer/schemaTable";
import { SchemaSelect } from "./SchemaSelect";

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 font-mono focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

interface TargetTablePanelProps {
  job: PEJob;
  projectId: string;
  onSaved: (updated: PEJob) => void;
  onDirtyChange: (dirty: boolean) => void;
  onConflict: () => void;
}

export function TargetTablePanel({
  job,
  projectId,
  onSaved,
  onDirtyChange,
  onConflict,
}: TargetTablePanelProps) {
  const initial = splitSchemaTable(job.targetTable);
  const [schemaName, setSchemaName] = useState(initial.schema);
  const [tableName, setTableName] = useState(initial.table);
  const [schemas, setSchemas] = useState<string[]>([]);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [schemaManualEntry, setSchemaManualEntry] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const targetIntrospect = useIntrospectTask(
    job.pipelineId,
    projectId,
    "target",
  );
  const tableNames =
    targetIntrospect.task?.result?.tables.map((t) => t.name) ?? [];

  const targetTable = joinSchemaTable(schemaName, tableName);
  const isDirty = targetTable !== job.targetTable;

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Reset local state when job prop changes (e.g. after save elsewhere)
  useEffect(() => {
    const parsed = splitSchemaTable(job.targetTable);
    setSchemaName(parsed.schema);
    setTableName(parsed.table);
  }, [job.id, job.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the target connection's real schemas once per job, so the picker
  // can offer them instead of forcing free-text schema entry.
  useEffect(() => {
    let cancelled = false;
    setLoadingSchemas(true);
    setSchemaError(null);
    listTargetSchemas(job.pipelineId)
      .then((list) => {
        if (cancelled) return;
        setSchemas(list);
        setSchemaManualEntry(
          list.length === 0 ||
            (initial.schema !== "" && !list.includes(initial.schema)),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setSchemaError(
          err instanceof Error ? err.message : "Failed to load schemas.",
        );
        setSchemaManualEntry(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingSchemas(false);
      });
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the pipeline's target connection could have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.pipelineId]);

  const handleBrowse = useCallback(async () => {
    setBrowsing(true);
    if (targetIntrospect.task?.status !== "completed") {
      await targetIntrospect.start(null, schemaName || null);
    }
  }, [targetIntrospect, schemaName]);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await updateJob(job.pipelineId, job.id, {
        target_table: targetTable.trim(),
      });
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      if (msg.includes("409") || msg.toLowerCase().includes("conflict")) {
        onConflict();
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [targetTable, job.pipelineId, job.id, onSaved, onConflict]);

  return (
    <div className="space-y-4">
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Database size={14} className="text-white/40" aria-hidden="true" />
          <label
            htmlFor="target-schema-select"
            className="text-xs font-medium text-white/60"
          >
            Target Schema
          </label>
        </div>
        <SchemaSelect
          id="target-schema-select"
          value={schemaName}
          schemas={schemas}
          loading={loadingSchemas}
          error={schemaError}
          manualEntry={schemaManualEntry}
          onManualEntryToggle={() => setSchemaManualEntry((prev) => !prev)}
          onChange={(value) => {
            setSchemaName(value);
            // Re-scope "Browse Existing Tables" to whatever schema is typed next.
            targetIntrospect.reset();
            setBrowsing(false);
          }}
        />

        <div className="flex items-center gap-2 pt-1">
          <label
            htmlFor="target-table-input"
            className="text-xs font-medium text-white/60"
          >
            Target Table
          </label>
        </div>
        <div className="flex gap-2">
          <input
            id="target-table-input"
            type="text"
            className={INPUT_CLASS}
            placeholder="table"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
          />
          <button
            type="button"
            onClick={() => void handleBrowse()}
            disabled={targetIntrospect.loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {targetIntrospect.loading && (
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            )}
            Browse Existing Tables
          </button>
        </div>

        {browsing && targetIntrospect.task?.status === "completed" && (
          <select
            aria-label="Select an existing target table"
            className="w-full bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white/80"
            value=""
            onChange={(e) => {
              if (e.target.value) setTableName(e.target.value);
            }}
          >
            <option value="">
              {tableNames.length === 0
                ? "— No existing tables found in this schema —"
                : "— Select a table to fill the field above —"}
            </option>
            {tableNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}

        {targetIntrospect.error !== null && (
          <p className="text-red-400 text-xs" role="alert">
            {targetIntrospect.error}
          </p>
        )}

        <p className="text-white/30 text-[10px]">
          Pick the schema this job's target table belongs to, then either map an
          existing table in it (via &quot;Browse Existing Tables&quot;) or type
          a new name — it is auto-created in that schema from the Column Mapping
          tab&apos;s mappings the first time this job runs. Use the Column
          Mapping tab&apos;s &quot;Auto-Map Columns&quot; button to (re)generate
          the mapping against whatever table is saved here.
        </p>
      </div>

      {saveError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {saveError}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!isDirty || saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <Save size={13} aria-hidden="true" />
          )}
          Save Target Table
        </button>
      </div>
    </div>
  );
}
